import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import InsertPhotoIcon from "@material-ui/icons/InsertPhoto";
import VideocamIcon from "@material-ui/icons/Videocam";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: 0,
    minHeight: 300,
  },
  listItem: {
    borderBottom: "1px solid #eee",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  searchBar: {
    padding: theme.spacing(1, 2),
    borderBottom: "1px solid #eee",
    position: "sticky",
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  }
}));

const FileManagerSelectorModal = ({ open, onClose, onSelect, filterType }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);

  useEffect(() => {
    if (open) {
      const delayDebounceFn = setTimeout(() => {
        setPageNumber(1);
        fetchFiles(1, searchParam, true);
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [open, searchParam]);

  const fetchFiles = async (page = 1, search = "", overwrite = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/files/", {
        params: { searchParam: search, pageNumber: page },
      });
      let extractedFiles = [];
      data.files.forEach(fList => {
        if (fList.options && fList.options.length > 0) {
          fList.options.forEach(opt => {
            extractedFiles.push(opt);
          });
        }
      });

      if (filterType) {
        extractedFiles = extractedFiles.filter(f => {
          const type = f.mediaType || "";
          const path = f.path || "";
          if (filterType === 'image') return type.startsWith('image') || path.match(/\.(jpg|jpeg|png|gif)$/i);
          if (filterType === 'video') return type.startsWith('video') || path.match(/\.(mp4|avi|mov|mkv)$/i);
          if (filterType === 'audio') return type.startsWith('audio') || path.match(/\.(mp3|ogg|wav)$/i);
          if (filterType === 'document') {
             const isMedia = type.startsWith('image') || type.startsWith('video') || type.startsWith('audio') || path.match(/\.(jpg|jpeg|png|gif|mp4|avi|mov|mkv|mp3|ogg|wav)$/i);
             return !isMedia;
          }
          return true;
        });
      }

      extractedFiles.sort((a, b) => b.id - a.id);

      setFiles(prev => overwrite ? extractedFiles : [...prev, ...extractedFiles]);
      setHasMore(data.hasMore);

      if (extractedFiles.length === 0 && data.hasMore) {
        setPageNumber(page + 1);
        fetchFiles(page + 1, search, false);
      }
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleDirectUpload = async (eOrFile, replace = false) => {
    let file = null;
    if (eOrFile?.target?.files) {
      file = eOrFile.target.files[0];
    } else {
      file = eOrFile;
    }
    if (!file) return;

    const fileName = file.name;

    setLoading(true);
    try {
      // Paso 1: Buscar o crear la lista con el mismo nombre del archivo
      let listId = null;
      try {
        const { data: searchData } = await api.get("/files/list", { params: { searchParam: fileName } });
        const targetList = Array.isArray(searchData)
          ? searchData.find(f => f.name.toLowerCase() === fileName.toLowerCase())
          : null;

        if (targetList) {
          listId = targetList.id;
        }
      } catch (errSearch) {
        console.warn("Could not find file via /files/list, fallback to /files/", errSearch);
      }

      if (!listId) {
        try {
          const { data: searchData } = await api.get("/files/", { params: { searchParam: fileName, pageNumber: 1 } });
          const targetList = searchData?.files?.find(f => f.name.toLowerCase() === fileName.toLowerCase());
          if (targetList) {
            listId = targetList.id;
          }
        } catch (errSearch) {
          console.warn("Could not find file via /files/", errSearch);
        }
      }

      if (!listId) {
        try {
          const { data: newList } = await api.post("/files", { name: fileName, message: "" });
          listId = newList.id;
        } catch (createErr) {
          const errCode = createErr.response?.data?.error;
          if (
            errCode === "ERR_FILE_NAME_ALREADY_EXISTS" ||
            errCode === "ERR_RATING_NAME_ALREADY_EXISTS" ||
            errCode === "ERR_FILE_ALREADY_EXISTS"
          ) {
            const { data: retryData } = await api.get("/files/list", { params: { searchParam: fileName } });
            const retryList = Array.isArray(retryData)
              ? retryData.find(f => f.name.toLowerCase() === fileName.toLowerCase())
              : null;
            if (retryList) {
              listId = retryList.id;
            } else {
              throw createErr;
            }
          } else {
            throw createErr;
          }
        }
      }

      // Paso 2: Subir el archivo a esa lista
      const formData = new FormData();
      formData.append("files", file);
      formData.append("fileId", listId);
      formData.append("mediaType", file.type || "application/octet-stream");
      if (replace) {
        formData.append("replace", "true");
      }

      await api.post(`/files/uploadList/${listId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const successMsg = replace
        ? (i18n.t("files.toasts.replaced") || "Archivo reemplazado correctamente.")
        : (i18n.t("files.toasts.uploaded") || "Archivo subido correctamente.");
      toast.success(successMsg);
      setPendingUploadFile(null);

      setSearchParam("");
      setPageNumber(1);
      fetchFiles(1, "", true);
    } catch (err) {
      const errCode = err.response?.data?.error;
      // Solo mostrar el modal de confirmación si NO estamos ya en modo reemplazo
      if (!replace && (
        errCode === "ERR_FILE_ALREADY_EXISTS" ||
        errCode === "ERR_FILE_NAME_ALREADY_EXISTS" ||
        errCode === "ERR_RATING_NAME_ALREADY_EXISTS"
      )) {
        setPendingUploadFile(file);
        setConfirmModalOpen(true);
      } else {
        toastError(err);
      }
    }
    setLoading(false);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + clientHeight) < 50) {
      setPageNumber(prev => prev + 1);
      fetchFiles(pageNumber + 1, searchParam, false);
    }
  };

  const getFileUrl = (path) => {
    if (!path) return "";
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
    
    if (path.startsWith("/")) return `${backendUrl}${path}`;
    
    if (path.startsWith("http")) {
      try {
        const urlObj = new URL(path);
        if (urlObj.pathname.startsWith('/public/')) {
          return `${backendUrl}${urlObj.pathname}`;
        }
      } catch (e) {}
      
      if (path.includes("localhost")) {
        return path.replace("http://localhost:8080", backendUrl).replace("http://localhost:3000", backendUrl);
      }
      
      return path;
    }
    
    return `${backendUrl}/${path}`;
  };

  const getIcon = (path, mediaType) => {
    if (!path) return <InsertDriveFileIcon />;
    if (path.endsWith('.mp4') || mediaType?.startsWith('video')) {
      return <VideocamIcon />;
    }
    if (path.endsWith('.mp3') || path.endsWith('.ogg') || mediaType?.startsWith('audio')) {
      return <AudiotrackIcon />;
    }
    return <InsertDriveFileIcon />;
  };

  const renderPreview = (file) => {
    const isImage = file.path?.endsWith('.jpg') || file.path?.endsWith('.png') || file.path?.endsWith('.jpeg') || file.path?.endsWith('.gif') || file.mediaType?.startsWith('image');
    const isVideo = file.path?.endsWith('.mp4') || file.path?.endsWith('.avi') || file.path?.endsWith('.mov') || file.mediaType?.startsWith('video');

    if (isImage) {
      return (
        <Avatar 
          src={getFileUrl(file.path)} 
          variant="rounded" 
          style={{ width: 80, height: 80, objectFit: 'cover', backgroundColor: '#eee' }} 
        />
      );
    }
    
    if (isVideo) {
      return (
        <div style={{ width: 80, height: 80, backgroundColor: '#000', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
          <video 
            src={`${getFileUrl(file.path)}#t=0.1`} 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            preload="metadata"
          />
          <IconButton 
            onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}
            style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)' }}
            size="small"
          >
            <VideocamIcon />
          </IconButton>
        </div>
      );
    }

    return (
      <Avatar className={classes.avatar} variant="rounded" style={{ width: 80, height: 80 }}>
        {getIcon(file.path, file.mediaType)}
      </Avatar>
    );
  };

  const handleSelect = (option) => {
    // Resolver la URL aquí mismo antes de pasarla al padre
    // Así siempre recibirá una URL limpia apuntando al backend correcto
    const resolvedUrl = getFileUrl(option.path);
    onSelect(resolvedUrl);
    onClose();
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {i18n.t("statusScheduler.files") || "Archivos Multimedia"}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent} onScroll={handleScroll} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        <div className={classes.searchBar} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={i18n.t("contacts.searchPlaceholder") || "Buscar..."}
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <input
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
            style={{ display: 'none' }}
            id="direct-upload-file"
            type="file"
            onChange={handleDirectUpload}
          />
          <label htmlFor="direct-upload-file" style={{ margin: 0 }}>
            <Button 
              variant="contained" 
              color="primary" 
              component="span"
              size="small"
              style={{ whiteSpace: 'nowrap', minWidth: 'max-content' }}
              startIcon={<CloudUploadIcon />}
              disabled={loading}
            >
              {i18n.t("fileModal.fileManager.uploadFile")}
            </Button>
          </label>
        </div>

        <List disablePadding>
          {files.length === 0 && !loading ? (
            <ListItem>
              <ListItemText primary={i18n.t("fileModal.fileManager.noFilesFound")} />
            </ListItem>
          ) : (
            files.map((file, idx) => (
              <ListItem 
                button 
                key={idx} 
                className={classes.listItem}
                onClick={() => handleSelect(file)}
              >
                <div style={{ marginRight: 16 }}>
                  {renderPreview(file)}
                </div>
                <ListItemText
                  primary={<Typography variant="subtitle1" style={{ fontWeight: 500 }}>{file.name}</Typography>}
                  secondary={`/files/${file.path.split('/').pop()} • ${formatBytes(file.size)}`}
                />
              </ListItem>
            ))
          )}
          {loading && (
            <div className={classes.loader} style={{ padding: 16 }}>
              <CircularProgress size={24} />
            </div>
          )}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {i18n.t("statusScheduler.cancel") || "Cancelar"}
        </Button>
      </DialogActions>

      {/* Video Preview Modal */}
      <Dialog open={!!previewFile} onClose={() => setPreviewFile(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {previewFile?.name || "Vista Previa"}
            <IconButton onClick={() => setPreviewFile(null)} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: 0, backgroundColor: '#000', display: 'flex', justifyContent: 'center' }}>
          {previewFile && (
            <video 
              src={getFileUrl(previewFile.path)} 
              controls 
              autoPlay 
              style={{ width: '100%', maxHeight: '70vh' }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        title={i18n.t("files.existingFileTitle") || "Archivo Existente"}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => {
          setConfirmModalOpen(false);
          handleDirectUpload(pendingUploadFile, true);
        }}
      >
        {i18n.t("files.existingFileMessage", { filename: pendingUploadFile?.name }) || `El archivo '${pendingUploadFile?.name}' ya existe. ¿Deseas reemplazarlo?`}
      </ConfirmationModal>
    </Dialog>
  );
};

export default FileManagerSelectorModal;
